import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Divider, Grid, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addCampaign, editCampaign } from '../../../../api/endpoints/cmsContent';
import type { Campaign } from '../../../../api/types/cmsContent';
import { FormDialog } from '../../../../components/common';
import { CampaignDetailsSection } from './components/CampaignDetailsSection';

const schema = z.object({
  airlineCode: z.string().optional(),
  airlineName: z.string().optional(),
  price: z.string().optional(),
  priceCurrency: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  link: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  airlineCode: '',
  airlineName: '',
  price: '',
  priceCurrency: 'TRY',
  from: '',
  to: '',
  startDate: '',
  endDate: '',
  link: '',
};

/** ISO date-time -> `YYYY-MM-DD` for the `type="date"` input, matching this codebase's
 *  existing convention (see modules/corporate/employee/components/EmployeeFormDialog.tsx). */
function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function toFormValues(campaign: Campaign): FormValues {
  return {
    airlineCode: campaign.airlineCode ?? '',
    airlineName: campaign.airlineName ?? '',
    price: campaign.price ?? '',
    priceCurrency: campaign.priceCurrency ?? 'TRY',
    from: campaign.from ?? '',
    to: campaign.to ?? '',
    startDate: toDateInputValue(campaign.startDate),
    endDate: toDateInputValue(campaign.endDate),
    link: campaign.link ?? '',
  };
}

/** Create/edit dialog for Kampanyalar (Campaign, wire object `AirCampaign`). Confirmed via
 *  local-backend swagger + full live curl round-trip (see api/types/cmsContent.ts header —
 *  note `price` here is a STRING field, unlike CheapestDestination's numeric `price`). There
 *  is no `GetCampaignById` route (confirmed) — `campaign` is the row straight from the list
 *  page's query cache, including its already-fetched `airCampaignDetails`. In edit mode, also
 *  renders `CampaignDetailsSection` — the ONLY UI for that child resource, since it has no
 *  standalone list route (see that component's doc comment and api/types/cmsContent.ts).
 *  Always rendered inside a specific site's tab, so `site_Id` is injected from the `siteId`
 *  prop. */
export function CampaignFormDialog({
  open,
  campaign,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  campaign: Campaign | null;
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!campaign;

  const {
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  useEffect(() => {
    if (open) reset(campaign ? toFormValues(campaign) : emptyValues);
  }, [open, campaign, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && campaign
        ? editCampaign({ id: campaign.id, site_Id: siteId, ...values })
        : addCampaign({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Kampanya güncellendi.' : 'Kampanya oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'campaigns'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Kampanyayı Güncelle' : 'Yeni Kampanya'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Havayolu Adı" {...register('airlineName')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Havayolu Kodu" {...register('airlineCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Kalkış" {...register('from')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Varış" {...register('to')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Başlangıç Tarihi"
                slotProps={{ inputLabel: { shrink: true } }}
                {...register('startDate')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Bitiş Tarihi"
                slotProps={{ inputLabel: { shrink: true } }}
                {...register('endDate')}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" label="Fiyat" {...register('price')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField fullWidth size="small" label="Para Birimi" {...register('priceCurrency')} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Link" {...register('link')} />
            </Grid>
          </Grid>

          {isEdit && campaign && (
            <>
              <Divider />
              <CampaignDetailsSection campaignId={campaign.id} initialDetails={campaign.airCampaignDetails ?? []} />
            </>
          )}

    </FormDialog>
  );
}
